/**
 * Image compression and dimension calculation utilities for Web Chat multimodal attachments.
 */

export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;

export type AllowedImageMimeType = typeof ALLOWED_IMAGE_TYPES[number];

export interface ImageCompressionResult {
  blob: Blob;
  width: number;
  height: number;
  wasDownscaled: boolean;
  mimeType: string;
}

/**
 * Format bytes into human-readable B, KB, or MB string.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Validates whether a file and mime type is an allowed image, strictly rejecting SVGs.
 */
export function isAllowedImageType(mimeType: string, fileName = ''): boolean {
  if (!mimeType) return false;
  const lowerMime = mimeType.toLowerCase();
  const lowerName = fileName.toLowerCase();

  // Explicit SVG rejection
  if (lowerMime === 'image/svg+xml' || lowerName.endsWith('.svg')) {
    return false;
  }

  return (ALLOWED_IMAGE_TYPES as readonly string[]).includes(lowerMime);
}

/**
 * Calculates proportional downscaled dimensions constrained to maxDimension.
 */
export function calculateDownscaleDimensions(
  width: number,
  height: number,
  maxDimension = 1600
): { needed: boolean; targetWidth: number; targetHeight: number } {
  if (width <= maxDimension && height <= maxDimension) {
    return {
      needed: false,
      targetWidth: width,
      targetHeight: height,
    };
  }

  let targetWidth = width;
  let targetHeight = height;

  if (width >= height) {
    targetHeight = Math.max(1, Math.round((height * maxDimension) / width));
    targetWidth = Math.max(1, maxDimension);
  } else {
    targetWidth = Math.max(1, Math.round((width * maxDimension) / height));
    targetHeight = Math.max(1, maxDimension);
  }

  return {
    needed: true,
    targetWidth,
    targetHeight,
  };
}

/**
 * Downscales an image client-side via HTML Canvas if file size exceeds threshold (default 2MB).
 * Images <= 2MB bypass downscaling and preserve the original File/Blob.
 * Downscaled images are exported as image/jpeg at specified quality (default 0.8).
 */
export async function downscaleImageIfNeeded(
  file: File,
  maxDimension = 1600,
  quality = 0.8,
  thresholdBytes = 2 * 1024 * 1024
): Promise<ImageCompressionResult> {
  // If <= 2MB, bypass downscaling completely
  if (file.size <= thresholdBytes) {
    return {
      blob: file,
      width: 0,
      height: 0,
      wasDownscaled: false,
      mimeType: file.type,
    };
  }

  return new Promise((resolve) => {
    // If not in a browser DOM environment with Canvas/Image support
    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      typeof Image === 'undefined'
    ) {
      return resolve({
        blob: file,
        width: 0,
        height: 0,
        wasDownscaled: false,
        mimeType: file.type,
      });
    }

    const img = new Image();
    let objectUrl = '';
    try {
      objectUrl = URL.createObjectURL(file);
    } catch {
      return resolve({
        blob: file,
        width: 0,
        height: 0,
        wasDownscaled: false,
        mimeType: file.type,
      });
    }

    img.onload = () => {
      try {
        URL.revokeObjectURL(objectUrl);
      } catch {}

      const { targetWidth, targetHeight } = calculateDownscaleDimensions(
        img.width,
        img.height,
        maxDimension
      );

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return resolve({
          blob: file,
          width: img.width,
          height: img.height,
          wasDownscaled: false,
          mimeType: file.type,
        });
      }

      try {
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        canvas.toBlob(
          (blob) => {
            canvas.width = 0;
            canvas.height = 0;
            if (blob) {
              resolve({
                blob,
                width: targetWidth,
                height: targetHeight,
                wasDownscaled: true,
                mimeType: 'image/jpeg',
              });
            } else {
              resolve({
                blob: file,
                width: img.width,
                height: img.height,
                wasDownscaled: false,
                mimeType: file.type,
              });
            }
          },
          'image/jpeg',
          quality
        );
      } catch {
        canvas.width = 0;
        canvas.height = 0;
        resolve({
          blob: file,
          width: img.width,
          height: img.height,
          wasDownscaled: false,
          mimeType: file.type,
        });
      }
    };

    img.onerror = () => {
      try {
        URL.revokeObjectURL(objectUrl);
      } catch {}

      resolve({
        blob: file,
        width: 0,
        height: 0,
        wasDownscaled: false,
        mimeType: file.type,
      });
    };

    img.src = objectUrl;
  });
}
